import React, { useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Popper,
  Select,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight, Check, House, Plus, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { parseISO, isWithinInterval, startOfDay, endOfDay, differenceInCalendarDays, isValid } from 'date-fns';
import {
  useGetFilteredTimeOffEntriesQuery,
  useGetTimeOffEntryByIdQuery,
  useUpdateTimeOffEntryMutation,
  TimeOffEntry,
  TimeOffType,
  TimeOffStatus,
} from '../store/slices/timeOffSlice';
import { useGetDepartmentsQuery } from '../store/slices/departmentSlice';
import { useGetEmployeesQuery, Employee } from '../store/slices/employeeSlice';
import { useDispatch } from 'react-redux';
import { openModal } from '../store/slices/uiSlice';
import { useAuth } from '../contexts/AuthContext';
import { sbg } from '../theme/sbgTokens';
import { AUTH_ROLE_LABELS, AuthRole, isManagerLike, isOwnRequestLocked, OWN_REQUEST_LOCKED_REASON, scopesEmployee } from '../utils/roles';
import { getTodayEt } from '../utils/easternTime';
import { useNow } from '../hooks/useNow';
import { apiErrorReason } from '../components/modals/time-off/timeOffHours';
import WfhApprovalRoute from '../components/time-off/WfhApprovalRoute';
import { WFH_POLICY_REASONS, wfhListFlag } from '../components/time-off/wfhRoute';
import {
  ConfirmDialog,
  CountBadge,
  DefinitionList,
  EmptyState,
  ErrorState,
  KpiGrid,
  KpiTile,
  Meter,
  PageHeader,
  PersonAvatar,
  PersonCell,
  SearchField,
  Sheet,
  StatusPill,
  TableCard,
  TableSkeletonRows,
  Tag,
  Toolbar,
} from '../components/ui';
import type { StatusTone } from '../components/ui';
import {
  MISSING,
  formatCount,
  formatDate,
  formatDateRange,
  formatDateTime,
  formatNumber,
  formatRelative,
  formatTime,
} from '../utils/format';

// ── Display helpers ──────────────────────────────────────────────────

type View = 'pending' | 'today' | 'all';

const TYPE_LABEL: Record<string, string> = {
  [TimeOffType.PTO]: 'PTO',
  [TimeOffType.SICK]: 'Sick',
  [TimeOffType.WFH]: 'WFH',
};

const STATUS_META: Record<string, { tone: StatusTone; label: string }> = {
  [TimeOffStatus.PENDING]: { tone: 'warning', label: 'Pending approval' },
  [TimeOffStatus.APPROVED]: { tone: 'success', label: 'Approved' },
  [TimeOffStatus.REJECTED]: { tone: 'danger', label: 'Declined' },
};

const REVIEW_REASON_LABEL: Record<string, string> = {
  pto_lead_time: 'Short notice: PTO requested with less than 2 weeks’ notice',
  wfh_not_approved: 'Not on a hybrid schedule: WFH needs VP approval',
  wfh_escalated_to_vp: 'Escalated to a VP for a decision',
  recurring_calendar_series: 'Recurring calendar series',
  uncategorized: 'Needs a category',
  openai_error: 'Couldn’t be categorized automatically',
  reanalyze_failed: 'Couldn’t be categorized automatically',
};

/** Short versions for the approval queue's Flag column; the full label sits in a tooltip. */
const REVIEW_FLAG_SHORT: Record<string, string> = {
  pto_lead_time: 'Short notice',
  wfh_not_approved: 'Not on hybrid schedule',
  wfh_escalated_to_vp: 'Escalated to a VP',
  recurring_calendar_series: 'Recurring series',
  uncategorized: 'Needs a category',
  openai_error: 'Not categorized',
  reanalyze_failed: 'Not categorized',
};

const sentenceCase = (value: string) => {
  const text = value.replace(/_/g, ' ').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : text;
};

const typeLabel = (type: string) => TYPE_LABEL[type] ?? type;
const statusMeta = (status: string) => STATUS_META[status] ?? { tone: 'neutral' as StatusTone, label: status };
const reviewReasonLabel = (reason: string) => REVIEW_REASON_LABEL[reason] ?? sentenceCase(reason);
const reviewFlagShort = (reason: string) => REVIEW_FLAG_SHORT[reason] ?? sentenceCase(reason);
const roleLabel = (role: string | null | undefined) =>
  role ? AUTH_ROLE_LABELS[role as AuthRole] ?? sentenceCase(role) : undefined;

/**
 * A VP review is still open. Same test the API uses before it refuses a
 * manager's approve/decline with 403 ("Only HR or an admin can override an
 * active VP review.").
 */
const isUnderVpReview = (entry: TimeOffEntry) =>
  entry.status === TimeOffStatus.PENDING
  && entry.type === TimeOffType.WFH
  && entry.reviewReason === 'wfh_escalated_to_vp'
  && !entry.vpDecidedAt;

const VP_REVIEW_STATUS: { tone: StatusTone; label: string } = { tone: 'info', label: 'VP review' };
const statusFor = (entry: TimeOffEntry) => (isUnderVpReview(entry) ? VP_REVIEW_STATUS : statusMeta(entry.status));

const VP_LOCKED_REASON = 'A VP is reviewing this request. HR can override it.';

/** The more recently changed copy of a request, by updatedAt; the first when they tie or can't be compared. */
export const newerOf = (a: TimeOffEntry, b: TimeOffEntry) => {
  const at = (entry: TimeOffEntry) => new Date(entry.updatedAt || 0).getTime();
  return at(b) > at(a) ? b : a;
};

/** "Starts in 14 days" against today's ET calendar date; null for unreadable dates. */
const startsLabel = (startDate: string, todayYmd: string) => {
  const start = parseISO(startDate);
  const today = parseISO(todayYmd);
  if (!isValid(start) || !isValid(today)) return null;
  const days = differenceInCalendarDays(start, today);
  if (days === 0) return 'Starts today';
  if (days === 1) return 'Starts tomorrow';
  if (days > 1) return `Starts in ${formatCount(days, 'day')}`;
  if (days === -1) return 'Started yesterday';
  return `Started ${formatCount(-days, 'day')} ago`;
};

/** Soonest first, then by name, so the queue leads with what needs a decision now. */
const byStartAsc = (a: TimeOffEntry, b: TimeOffEntry) =>
  a.startDate.localeCompare(b.startDate) || a.employee.name.localeCompare(b.employee.name);

type SortKey = 'dates' | 'submitted';
type SortDirection = 'asc' | 'desc';

const formatHours = (hours: number | null | undefined) => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) return MISSING;
  return `${formatNumber(hours, Number.isInteger(hours) ? 0 : 1)} ${hours === 1 ? 'hour' : 'hours'}`;
};

const possessive = (name: string) => (name.endsWith('s') ? `${name}’` : `${name}’s`);

const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;

/** Wraps a disabled control so its tooltip still opens on hover and focus. */
const ExplainedDisabled: React.FC<{ reason: string | null; children: React.ReactElement }> = ({ reason, children }) =>
  reason ? (
    <Tooltip title={reason}>
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {children}
      </Box>
    </Tooltip>
  ) : (
    children
  );

// ── Balance meters (hover card and request sheet) ───────────────────

const BalanceMeters: React.FC<{ employee: Employee }> = ({ employee }) => {
  const rows = [
    { label: 'PTO', used: employee.ptoUsed || 0, total: employee.ptoAllotment || 0 },
    { label: 'Sick time', used: employee.sickTimeUsed || 0, total: employee.sickTimeAllotment || 0 },
  ];
  return (
    <Box sx={{ display: 'grid', gap: 1.5 }}>
      {rows.map((row) => {
        const over = row.total > 0 && row.used > row.total;
        const left = Math.max(row.total - row.used, 0);
        return (
          <Box key={row.label} sx={{ display: 'grid', gap: 0.5 }}>
            <Meter
              label={row.label}
              value={row.used}
              max={row.total || 1}
              tone={over ? 'danger' : 'brand'}
              valueLabel={row.total > 0 ? `${formatNumber(row.used)} of ${formatHours(row.total)}` : `${formatHours(row.used)} used`}
              aria-label={`${row.label} used`}
            />
            <Typography variant="caption" component="p" sx={{ color: over ? sbg.status.danger.text : sbg.text.secondary, ...TABULAR }}>
              {row.total <= 0
                ? 'No allotment on file'
                : over
                  ? `${formatHours(row.used - row.total)} over the allotment`
                  : `${formatHours(left)} left`}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const EmployeeHoverCard: React.FC<{ employee: Employee }> = ({ employee }) => (
  <Box sx={{ width: 300, p: 2, display: 'grid', gap: 1.5 }}>
    <PersonCell
      name={employee.name}
      secondary={[employee.department?.name, roleLabel(employee.role)].filter(Boolean).join(' · ') || undefined}
    />
    <Box sx={{ borderTop: `1px solid ${sbg.border.subtle}`, pt: 1.5 }}>
      <BalanceMeters employee={employee} />
    </Box>
    <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <House size={14} aria-hidden="true" />
      {employee.wfhEligible ? 'Hybrid schedule' : 'In-office schedule'}
    </Typography>
  </Box>
);

// ── Toolbar filter select: the value carries its own visible label ──

const FilterSelect: React.FC<{
  noun: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}> = ({ noun, value, onChange, options }) => (
  <Select
    size="small"
    value={value}
    displayEmpty
    onChange={(event) => onChange(String(event.target.value))}
    renderValue={(selected) => {
      const match = options.find((option) => option.value === selected);
      return (
        <Box component="span" sx={{ color: match ? sbg.text.primary : sbg.text.secondary }}>
          {`${noun}: `}
          <Box component="span" sx={{ color: sbg.text.primary }}>{match ? match.label : 'All'}</Box>
        </Box>
      );
    }}
    inputProps={{ 'aria-label': `Filter by ${noun.toLowerCase()}` }}
    MenuProps={{ slotProps: { paper: { sx: { maxHeight: 360 } } } }}
    sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 160 }, maxWidth: 240, flex: { xs: '1 1 calc(50% - 4px)', sm: '0 0 auto' } }}
  >
    <MenuItem value="">All</MenuItem>
    {options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ))}
  </Select>
);

// ── Main component ───────────────────────────────────────────────────

const TimeOff: React.FC = () => {
  const dispatch = useDispatch();
  const { profile, user } = useAuth();
  const role = profile?.role || 'employee';
  const today = useMemo(() => new Date(), []);
  const todayYmd = useMemo(() => getTodayEt(), []);
  // Re-read each minute: a VP deadline can pass while this list stays open.
  const now = useNow();

  const [view, setView] = useState<View>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [hoverAnchor, setHoverAnchor] = useState<HTMLElement | null>(null);
  const [hoverEmployee, setHoverEmployee] = useState<Employee | null>(null);
  const hoverTimer = useRef<number | undefined>(undefined);
  const [selectedSnapshot, setSelectedEntry] = useState<TimeOffEntry | null>(null);
  const [declineTarget, setDeclineTarget] = useState<TimeOffEntry | null>(null);
  // Every request with a decision in flight, so a slow approve on one row never
  // unlocks (or blocks) another row's buttons or the decline dialog.
  const [inFlight, setInFlight] = useState<Record<string, 'approve' | 'decline'>>({});
  const inFlightRef = useRef(new Set<string>());
  const [sort, setSort] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'submitted', direction: 'desc' });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { data: departments = [], isLoading: departmentsLoading, error: departmentsError, refetch: reloadDepartments } = useGetDepartmentsQuery();
  const { data: employees = [], isLoading: employeesLoading, error: employeesError, refetch: reloadEmployees } = useGetEmployeesQuery();
  const currentYearStart = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-01-01`;
  }, []);
  const {
    data: timeOffEntries = [],
    isLoading,
    error: entriesError,
    refetch: reloadEntries,
    fulfilledTimeStamp,
  } = useGetFilteredTimeOffEntriesQuery({ startDate: currentYearStart });
  const [updateTimeOffEntry] = useUpdateTimeOffEntryMutation();
  // The sheet shows the request as the server has it now, not the row as it
  // was clicked: a VP's decision from an email link, or a claim, changes it
  // while the sheet is open, and the approval route's refresh refetches this.
  // Opening re-reads the request, and the list row wins while it's newer
  // (the list may have reloaded since the by-id copy was cached).
  const { currentData: selectedFresh } = useGetTimeOffEntryByIdQuery(selectedSnapshot?._id ?? skipToken, { refetchOnMountOrArgChange: true });
  const selectedEntry = selectedSnapshot && selectedFresh?._id === selectedSnapshot._id ? newerOf(selectedFresh, selectedSnapshot) : selectedSnapshot;

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);

  /** The signed-in person's own employee record: who "Request time off" files for. */
  const currentEmployee = useMemo(
    () => employees.find((employee) => employee.userId === user?.id) ?? null,
    [employees, user?.id]
  );
  const currentManagerName = currentEmployee?.name || null;

  const managerDepartmentIds = useMemo(() => {
    if (role !== 'manager' || !user?.id) return [];
    return departments.filter(dept => dept.managerUserId === user.id).map(dept => dept._id);
  }, [departments, role, user?.id]);

  const visibleTimeOffEntries = useMemo(() => {
    if (!isManagerLike(role)) return timeOffEntries;
    return timeOffEntries.filter((entry) => scopesEmployee(entry.employee, {
      role,
      userId: user?.id,
      managerName: currentManagerName,
      managerDepartmentIds,
    }));
  }, [currentManagerName, managerDepartmentIds, role, timeOffEntries, user?.id]);

  const pendingEntries = useMemo(() => {
    return visibleTimeOffEntries.filter(e => {
      if (e.status !== TimeOffStatus.PENDING || e.hours === 0) return false;
      if (role === 'employee') {
        return false;
      }
      return true;
    }).sort(byStartAsc);
  }, [role, visibleTimeOffEntries]);

  const todayEntries = useMemo(() =>
    visibleTimeOffEntries.filter(e => {
      if (e.status === TimeOffStatus.REJECTED || e.hours === 0) return false;
      try {
        const start = startOfDay(parseISO(e.startDate));
        const end = endOfDay(parseISO(e.endDate));
        if (!isWithinInterval(today, { start, end })) return false;
      } catch { return false; }
      return true;
    }).sort(byStartAsc),
    [today, visibleTimeOffEntries]
  );

  const matchesSearch = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (e: TimeOffEntry) => !term || e.employee.name.toLowerCase().includes(term) || Boolean(e.notes?.toLowerCase().includes(term));
  }, [searchTerm]);

  const filteredEntries = useMemo(() => {
    let entries = visibleTimeOffEntries;
    if (employeeFilter) entries = entries.filter(e => e.employee._id === employeeFilter);
    if (departmentFilter) entries = entries.filter(e => e.employee.department?._id === departmentFilter);
    if (typeFilter) entries = entries.filter(e => e.type === typeFilter);
    if (statusFilter) entries = entries.filter(e => e.status === statusFilter);
    const sign = sort.direction === 'asc' ? 1 : -1;
    return entries.filter(matchesSearch).sort((a, b) => {
      const primary = sort.key === 'dates'
        ? a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate)
        : (a.createdAt || '').localeCompare(b.createdAt || '');
      return sign * primary || a.employee.name.localeCompare(b.employee.name);
    });
  }, [visibleTimeOffEntries, employeeFilter, departmentFilter, typeFilter, statusFilter, matchesSearch, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'desc' }
    );
    setPage(0);
  };

  const hasActiveFilters = Boolean(employeeFilter || departmentFilter || typeFilter || statusFilter);
  const hasQuery = Boolean(searchTerm.trim()) || (view === 'all' && hasActiveFilters);

  const viewSource = view === 'pending' ? pendingEntries : view === 'today' ? todayEntries : visibleTimeOffEntries;
  const viewRows = view === 'pending'
    ? pendingEntries.filter(matchesSearch)
    : view === 'today'
      ? todayEntries.filter(matchesSearch)
      : filteredEntries;
  const pageRows = view === 'all' ? viewRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : viewRows;

  const clearFilters = () => {
    setSearchTerm('');
    setEmployeeFilter('');
    setDepartmentFilter('');
    setTypeFilter('');
    setStatusFilter('');
    setPage(0);
  };

  const closeSnackbar = () => setSnackbar(s => ({ ...s, open: false }));

  const markInFlight = (id: string, kind: 'approve' | 'decline' | null) => {
    if (kind) inFlightRef.current.add(id);
    else inFlightRef.current.delete(id);
    setInFlight((current) => {
      const next = { ...current };
      if (kind) next[id] = kind;
      else delete next[id];
      return next;
    });
  };

  /** "Couldn’t approve Maria Chen’s request." plus the API's own reason when it gave one. */
  const failureMessage = (verb: 'approve' | 'decline', entry: TimeOffEntry, err: unknown) => {
    const reason = apiErrorReason(err);
    return `Couldn’t ${verb} ${possessive(entry.employee.name)} request. ${reason ?? 'The server didn’t respond. Try again in a minute.'}`;
  };

  const handleApprove = async (entry: TimeOffEntry) => {
    if (inFlightRef.current.has(entry._id)) return;
    markInFlight(entry._id, 'approve');
    try {
      await updateTimeOffEntry({ id: entry._id, timeOff: { status: TimeOffStatus.APPROVED } }).unwrap();
      setSnackbar({ open: true, message: `Approved ${possessive(entry.employee.name)} ${typeLabel(entry.type)} for ${formatDateRange(entry.startDate, entry.endDate)}.`, severity: 'success' });
      setSelectedEntry((current) => (current?._id === entry._id ? null : current));
    } catch (err) {
      setSnackbar({ open: true, message: failureMessage('approve', entry, err), severity: 'error' });
    } finally {
      markInFlight(entry._id, null);
    }
  };

  const handleDecline = async (entry: TimeOffEntry) => {
    if (inFlightRef.current.has(entry._id)) return;
    markInFlight(entry._id, 'decline');
    try {
      await updateTimeOffEntry({ id: entry._id, timeOff: { status: TimeOffStatus.REJECTED } }).unwrap();
      setSnackbar({ open: true, message: `Declined ${possessive(entry.employee.name)} ${typeLabel(entry.type)} for ${formatDateRange(entry.startDate, entry.endDate)}.`, severity: 'success' });
      setSelectedEntry((current) => (current?._id === entry._id ? null : current));
    } catch (err) {
      setSnackbar({ open: true, message: failureMessage('decline', entry, err), severity: 'error' });
    } finally {
      markInFlight(entry._id, null);
      setDeclineTarget((current) => (current?._id === entry._id ? null : current));
    }
  };

  const closeHover = () => {
    window.clearTimeout(hoverTimer.current);
    setHoverAnchor(null);
    setHoverEmployee(null);
  };

  const handleHover = (event: React.MouseEvent<HTMLElement>, employeeId: string) => {
    const emp = employees.find(e => e._id === employeeId);
    if (!emp) return;
    const target = event.currentTarget;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      setHoverEmployee(emp);
      setHoverAnchor(target);
    }, 450);
  };

  const findEmployee = (id: string) => employees.find(e => e._id === id);

  // A Jr manager's employee list holds only their assigned reps (never their own
  // record), and POST /time-off answers 403 to a Jr manager filing for anyone
  // outside those reps, themselves included.
  const requestBlockedReason = currentEmployee
    ? null
    : role === 'jr_ae'
      ? 'Jr managers can’t file their own time off here. Ask HR to add it for you.'
      : employeesLoading
        ? 'Checking your employee record…'
        : employeesError
          ? 'Your employee record didn’t load, so this can’t be filed yet. Retry below.'
          : 'Your sign-in isn’t linked to an employee record, so there’s no one to file this for. Ask an admin to link it.';

  /**
   * Files a request for the signed-in person's own employee record. The form
   * sends it (POST /time-off) and raises the toast itself, so only plain data
   * goes into the modal state. The API decides the status: sick time and
   * eligible WFH are approved at once, PTO waits on a manager.
   */
  const openRequestForm = () => {
    if (requestBlockedReason || !currentEmployee) return;
    dispatch(openModal({ type: 'time-off-form', data: { isEdit: false, employeeId: currentEmployee._id } }));
  };

  const requestButton = (variant: 'contained' | 'outlined') => (
    <ExplainedDisabled reason={requestBlockedReason}>
      <Button variant={variant} startIcon={<Plus size={16} />} onClick={openRequestForm} disabled={Boolean(requestBlockedReason)}>
        Request time off
      </Button>
    </ExplainedDisabled>
  );

  const loading = isLoading || departmentsLoading || employeesLoading;
  const failed = Boolean(entriesError || departmentsError || employeesError);
  const retryAll = () => { reloadEntries(); reloadDepartments(); reloadEmployees(); };

  const isAdminScope = role === 'admin' || role === 'hr';
  /**
   * Approve/decline is locked for this person on their own request and while
   * a VP review is open; the API answers 403 to both.
   */
  const lockedReasonFor = (entry: TimeOffEntry) => {
    if (isOwnRequestLocked(entry.employee, { role, userId: user?.id })) return OWN_REQUEST_LOCKED_REASON;
    return !isAdminScope && isUnderVpReview(entry) ? VP_LOCKED_REASON : null;
  };
  const sinceLabel = `Starting ${formatDate(currentYearStart)} or later`;
  const outToday = todayEntries.filter(e => e.type !== TimeOffType.WFH).length;
  const wfhToday = todayEntries.filter(e => e.type === TimeOffType.WFH).length;
  const kpiValue = (n: number) => (loading || failed ? MISSING : formatNumber(n));

  // Columns: Employee · Type · Dates · Duration · Status · Submitted · (Notes | Actions)
  const columnCount = 7;

  const renderRow = (entry: TimeOffEntry) => {
    const status = statusFor(entry);
    const isPending = entry.status === TimeOffStatus.PENDING;
    const busyKind = inFlight[entry._id];
    const busy = Boolean(busyKind);
    const locked = lockedReasonFor(entry);
    const starts = view === 'pending' ? startsLabel(entry.startDate, todayYmd) : null;
    const wfhFlag = view === 'pending' ? wfhListFlag(entry, now) : null;
    const open = () => {
      closeHover();
      setSelectedEntry(entry);
    };
    return (
      <TableRow
        key={entry._id}
        hover
        tabIndex={0}
        onClick={open}
        onKeyDown={(event) => {
          if (event.target !== event.currentTarget) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            open();
          }
        }}
        aria-label={`${entry.employee.name}, ${typeLabel(entry.type)}, ${formatDateRange(entry.startDate, entry.endDate)}, ${status.label}`}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell sx={{ minWidth: 220 }}>
          <Box
            sx={{ display: 'inline-flex', maxWidth: '100%' }}
            onMouseEnter={(e) => handleHover(e, entry.employee._id)}
            onMouseLeave={closeHover}
          >
            <PersonCell name={entry.employee.name} secondary={entry.employee.department?.name || undefined} />
          </Box>
        </TableCell>
        <TableCell>
          <Tag>{typeLabel(entry.type)}</Tag>
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', ...TABULAR }}>
          {formatDateRange(entry.startDate, entry.endDate)}
          {starts && (
            <Typography variant="body2" component="span" sx={{ display: 'block', color: sbg.text.secondary, ...TABULAR }}>
              {starts}
            </Typography>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap', ...TABULAR }}>
          {formatHours(entry.hours)}
        </TableCell>
        <TableCell>
          {view === 'pending' ? (
            wfhFlag ? (
              <Tooltip title={wfhFlag.detail}>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <StatusPill tone={wfhFlag.tone}>{wfhFlag.label}</StatusPill>
                </Box>
              </Tooltip>
            ) : isUnderVpReview(entry) ? (
              <Tooltip title={entry.vpDecisionDueAt ? `A VP decision is due by ${formatDateTime(entry.vpDecisionDueAt)}.` : 'A VP is reviewing this request.'}>
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <StatusPill tone={VP_REVIEW_STATUS.tone}>{VP_REVIEW_STATUS.label}</StatusPill>
                </Box>
              </Tooltip>
            ) : entry.reviewReason ? (
              <Tooltip title={reviewReasonLabel(entry.reviewReason)}>
                <Typography variant="body1" component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap' }}>
                  {reviewFlagShort(entry.reviewReason)}
                </Typography>
              </Tooltip>
            ) : (
              <Box component="span" sx={{ color: sbg.text.secondary }}>{MISSING}</Box>
            )
          ) : (
            <StatusPill tone={status.tone}>{status.label}</StatusPill>
          )}
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', color: sbg.text.secondary }}>
          {entry.createdAt ? (
            <Tooltip title={formatDateTime(entry.createdAt)}>
              <Box component="span" sx={TABULAR}>{formatRelative(entry.createdAt)}</Box>
            </Tooltip>
          ) : MISSING}
        </TableCell>
        {view === 'pending' ? (
          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
            {isPending && (
              <Box sx={{ display: 'inline-flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                <ExplainedDisabled reason={locked}>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<X size={14} />}
                    disabled={busy || Boolean(locked)}
                    aria-label={`Decline ${possessive(entry.employee.name)} request`}
                    onClick={(e) => { e.stopPropagation(); setDeclineTarget(entry); }}
                  >
                    Decline
                  </Button>
                </ExplainedDisabled>
                <ExplainedDisabled reason={locked}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={busyKind === 'approve' ? <CircularProgress size={14} thickness={5} color="inherit" aria-hidden="true" /> : <Check size={14} />}
                    disabled={busy || Boolean(locked)}
                    aria-busy={busyKind === 'approve' || undefined}
                    aria-label={`Approve ${possessive(entry.employee.name)} request`}
                    onClick={(e) => { e.stopPropagation(); handleApprove(entry); }}
                  >
                    {busyKind === 'approve' ? 'Approving…' : 'Approve'}
                  </Button>
                </ExplainedDisabled>
              </Box>
            )}
          </TableCell>
        ) : (
          <TableCell sx={{ maxWidth: 240, color: sbg.text.secondary }}>
            {entry.notes ? (
              <Tooltip title={entry.notes}>
                <Typography variant="body1" component="span" noWrap sx={{ display: 'block', color: sbg.text.secondary }}>
                  {entry.notes}
                </Typography>
              </Tooltip>
            ) : MISSING}
          </TableCell>
        )}
      </TableRow>
    );
  };

  const emptyCopy: Record<View, { title: string; body: string }> = {
    pending: {
      title: 'No requests waiting for approval',
      body: 'New requests appear here as soon as they’re submitted or synced from Outlook.',
    },
    today: {
      title: 'No one is out today',
      body: 'People on PTO, sick time or working from home today appear here.',
    },
    all: {
      title: 'No requests starting this year or later',
      body: 'Requests appear here once they’re submitted or synced from Outlook.',
    },
  };

  const renderState = () => {
    if (failed) {
      return (
        <ErrorState
          title="Couldn’t load time-off requests"
          body="The server didn’t respond. Try again in a minute; your search and filters are kept."
          onRetry={retryAll}
        />
      );
    }
    if (viewSource.length > 0 && hasQuery) {
      const term = searchTerm.trim();
      return (
        <EmptyState
          compact
          title="No requests match these filters"
          body={term ? `Nothing in this view matches “${term}”. Check the spelling or clear the filters.` : 'Try a different employee, department, type or status.'}
          action={<Button variant="outlined" onClick={clearFilters}>Clear filters</Button>}
        />
      );
    }
    const copy = emptyCopy[view];
    return (
      <EmptyState
        title={copy.title}
        body={copy.body}
        action={view === 'all' ? requestButton('outlined') : undefined}
      />
    );
  };

  const showTable = !failed && (loading || viewRows.length > 0);

  const viewLabel: Record<View, string> = { pending: 'Pending approval', today: 'Today', all: 'All requests' };
  const tabCount = (n: number) => (loading || failed ? undefined : n);

  const toolbar = (
    <Toolbar
      end={
        !loading && !failed ? (
          <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, ...TABULAR }} aria-live="polite">
            {formatCount(viewRows.length, 'request')}
          </Typography>
        ) : undefined
      }
    >
      <SearchField
        label="Search by name or notes"
        value={searchTerm}
        onChange={(value) => { setSearchTerm(value); setPage(0); }}
        width={260}
      />
      {view === 'all' && (
        <>
          <FilterSelect
            noun="Employee"
            value={employeeFilter}
            onChange={(value) => { setEmployeeFilter(value); setPage(0); }}
            options={[...employees].sort((a, b) => a.name.localeCompare(b.name)).map(e => ({ value: e._id, label: e.name }))}
          />
          <FilterSelect
            noun="Department"
            value={departmentFilter}
            onChange={(value) => { setDepartmentFilter(value); setPage(0); }}
            options={departments.map(d => ({ value: d._id, label: d.name }))}
          />
          <FilterSelect
            noun="Type"
            value={typeFilter}
            onChange={(value) => { setTypeFilter(value); setPage(0); }}
            options={Object.values(TimeOffType).map(t => ({ value: t, label: typeLabel(t) }))}
          />
          <FilterSelect
            noun="Status"
            value={statusFilter}
            onChange={(value) => { setStatusFilter(value); setPage(0); }}
            options={Object.values(TimeOffStatus).map(s => ({ value: s, label: statusMeta(s).label }))}
          />
        </>
      )}
      {hasQuery && (
        <Button variant="text" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </Toolbar>
  );

  // ── Sheet for the selected request ─────────────────────────────────
  const selectedEmployee = selectedEntry ? findEmployee(selectedEntry.employee._id) : undefined;
  const selectedStatus = selectedEntry ? statusFor(selectedEntry) : null;
  const selectedBusyKind = selectedEntry ? inFlight[selectedEntry._id] : undefined;
  const selectedBusy = Boolean(selectedBusyKind);
  const selectedLocked = selectedEntry ? lockedReasonFor(selectedEntry) : null;

  const sortHead = (key: SortKey, label: string) => (
    <TableSortLabel
      active={sort.key === key}
      direction={sort.key === key ? sort.direction : 'desc'}
      onClick={() => toggleSort(key)}
    >
      {label}
    </TableSortLabel>
  );
  const ariaSort = (key: SortKey) => (view === 'all' && sort.key === key ? sort.direction : false);

  return (
    <Box>
      <PageHeader
        overline="Attendance"
        title="Time off"
        meta={[
          isAdminScope ? 'All departments' : 'Your team',
          <Box key="period" component="span" sx={TABULAR}>{sinceLabel}</Box>,
          fulfilledTimeStamp ? (
            <Tooltip key="fresh" title={formatDateTime(fulfilledTimeStamp)}>
              <Box component="span" sx={TABULAR}>{`Updated ${formatTime(fulfilledTimeStamp)}`}</Box>
            </Tooltip>
          ) : null,
        ]}
        actions={requestButton('contained')}
      />

      <KpiGrid phoneColumns={2}>
        <KpiTile
          size="md"
          label="Pending"
          value={kpiValue(pendingEntries.length)}
          caption="Waiting on a decision"
          loading={loading}
          onClick={() => { setView('pending'); setPage(0); }}
          aria-label={`Pending approval: ${kpiValue(pendingEntries.length)}. Show pending requests.`}
        />
        <KpiTile
          size="md"
          label="Out today"
          value={kpiValue(outToday)}
          caption={formatDate(today, { weekday: true })}
          loading={loading}
          onClick={() => { setView('today'); setPage(0); }}
          aria-label={`Out today: ${kpiValue(outToday)}. Show who is away today.`}
        />
        <KpiTile
          size="md"
          label="WFH today"
          value={kpiValue(wfhToday)}
          caption="Working from home"
          loading={loading}
          onClick={() => { setView('today'); setPage(0); }}
          aria-label={`Working from home today: ${kpiValue(wfhToday)}. Show who is away today.`}
        />
        <KpiTile
          size="md"
          label="All requests"
          value={kpiValue(visibleTimeOffEntries.length)}
          caption={`Starting ${formatDate(currentYearStart, { short: true })} or later`}
          loading={loading}
          onClick={() => { setView('all'); setPage(0); }}
          aria-label={`All requests starting ${formatDate(currentYearStart)} or later: ${kpiValue(visibleTimeOffEntries.length)}. Show all requests.`}
        />
      </KpiGrid>

      <Box component="section" aria-label="Time-off requests" sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
        <Tabs
          value={view}
          onChange={(_, next: View) => { setView(next); setPage(0); }}
          aria-label="Time-off views"
          variant="scrollable"
          scrollButtons={false}
        >
          {(['pending', 'today', 'all'] as View[]).map((value) => {
            const count = tabCount(value === 'pending' ? pendingEntries.length : value === 'today' ? todayEntries.length : visibleTimeOffEntries.length);
            return (
              <Tab
                key={value}
                value={value}
                id={`time-off-tab-${value}`}
                aria-controls="time-off-panel"
                label={
                  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                    {viewLabel[value]}
                    {count !== undefined && <CountBadge count={count} aria-label={formatCount(count, 'request')} />}
                  </Box>
                }
              />
            );
          })}
        </Tabs>

        <Box role="tabpanel" id="time-off-panel" aria-labelledby={`time-off-tab-${view}`} sx={{ minWidth: 0 }}>
          <TableCard
            aria-label={`${viewLabel[view]} table`}
            toolbar={toolbar}
            footer={
              view === 'all' && !loading && !failed && viewRows.length > 0 ? (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={viewRows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(_, p) => setPage(p)}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  labelRowsPerPage="Rows per page"
                  sx={TABULAR}
                />
              ) : undefined
            }
          >
            {showTable ? (
              <Table sx={{ minWidth: 920 }} aria-label={viewLabel[view]}>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell sortDirection={ariaSort('dates')}>{view === 'all' ? sortHead('dates', 'Dates') : 'Dates'}</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell>{view === 'pending' ? 'Flag' : 'Status'}</TableCell>
                    <TableCell sortDirection={ariaSort('submitted')}>{view === 'all' ? sortHead('submitted', 'Submitted') : 'Submitted'}</TableCell>
                    {view === 'pending' ? (
                      <TableCell align="right">
                        <span className="sbg-sr-only">Actions</span>
                      </TableCell>
                    ) : (
                      <TableCell>Notes</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>{loading ? <TableSkeletonRows columns={columnCount} /> : pageRows.map(renderRow)}</TableBody>
              </Table>
            ) : (
              renderState()
            )}
          </TableCard>
        </Box>
      </Box>

      <Popper
        open={Boolean(hoverAnchor) && Boolean(hoverEmployee)}
        anchorEl={hoverAnchor}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.tooltip, pointerEvents: 'none' }}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      >
        <Paper
          elevation={8}
          sx={{ bgcolor: sbg.bg.raised, border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.lg, boxShadow: sbg.shadow.md }}
        >
          {hoverEmployee && <EmployeeHoverCard employee={hoverEmployee} />}
        </Paper>
      </Popper>

      <Sheet
        open={Boolean(selectedEntry)}
        onClose={() => setSelectedEntry(null)}
        title={selectedEntry ? selectedEntry.employee.name : ''}
        subtitle={
          selectedEntry && selectedStatus ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', ...TABULAR }}>
              {`${typeLabel(selectedEntry.type)} · ${formatDateRange(selectedEntry.startDate, selectedEntry.endDate)}`}
              <StatusPill tone={selectedStatus.tone}>{selectedStatus.label}</StatusPill>
            </Box>
          ) : undefined
        }
        actions={
          selectedEntry ? (
            <>
              <Button
                component={Link}
                to={`/time-off/${selectedEntry._id}`}
                variant="text"
                endIcon={<ArrowRight size={16} />}
                sx={{ mr: 'auto' }}
              >
                Open request
              </Button>
              {selectedEntry.status === TimeOffStatus.PENDING ? (
                <>
                  <ExplainedDisabled reason={selectedLocked}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<X size={16} />}
                      disabled={selectedBusy || Boolean(selectedLocked)}
                      onClick={() => setDeclineTarget(selectedEntry)}
                    >
                      Decline
                    </Button>
                  </ExplainedDisabled>
                  <ExplainedDisabled reason={selectedLocked}>
                    <Button
                      variant="contained"
                      startIcon={selectedBusyKind === 'approve' ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : <Check size={16} />}
                      disabled={selectedBusy || Boolean(selectedLocked)}
                      aria-busy={selectedBusyKind === 'approve' || undefined}
                      onClick={() => handleApprove(selectedEntry)}
                    >
                      {selectedBusyKind === 'approve' ? 'Approving…' : 'Approve request'}
                    </Button>
                  </ExplainedDisabled>
                </>
              ) : (
                <Button variant="outlined" onClick={() => setSelectedEntry(null)}>
                  Close
                </Button>
              )}
            </>
          ) : undefined
        }
      >
        {selectedEntry && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PersonAvatar name={selectedEntry.employee.name} size={40} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="labelMd" component="p" sx={{ color: sbg.text.primary }}>
                  {[selectedEntry.employee.department?.name, roleLabel(selectedEntry.employee.role)].filter(Boolean).join(' · ') || MISSING}
                </Typography>
                <Button
                  component={Link}
                  to={`/employees/${selectedEntry.employee._id}`}
                  variant="text"
                  size="small"
                  sx={{ ml: -1.25, mt: 0.25 }}
                >
                  View profile
                </Button>
              </Box>
            </Box>

            <WfhApprovalRoute entry={selectedEntry} />

            <DefinitionList
              items={[
                { label: 'Type', value: <Tag>{typeLabel(selectedEntry.type)}</Tag> },
                { label: 'Dates', value: <Box component="span" sx={TABULAR}>{formatDateRange(selectedEntry.startDate, selectedEntry.endDate)}</Box> },
                { label: 'Duration', value: <Box component="span" sx={TABULAR}>{formatHours(selectedEntry.hours)}</Box> },
                { label: 'Manager', value: selectedEntry.employee.managerName || MISSING },
                { label: 'Source', value: selectedEntry.isFromCalendar ? 'Outlook calendar' : 'Added in Attendance' },
                // The approval route above explains a WFH policy hold in full.
                ...(selectedEntry.reviewReason && !(selectedEntry.type === TimeOffType.WFH && WFH_POLICY_REASONS.has(selectedEntry.reviewReason))
                  ? [{ label: 'Review flag', value: reviewReasonLabel(selectedEntry.reviewReason) }]
                  : []),
                ...(selectedEntry.originalCalendarSubject ? [{ label: 'Calendar subject', value: selectedEntry.originalCalendarSubject }] : []),
                {
                  label: 'Submitted',
                  value: selectedEntry.createdAt ? <Box component="span" sx={TABULAR}>{formatDateTime(selectedEntry.createdAt)}</Box> : MISSING,
                },
              ]}
            />

            {selectedLocked && (
              <Alert severity="info">{selectedLocked}</Alert>
            )}

            <Box sx={{ display: 'grid', gap: 0.75 }}>
              <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>Notes</Typography>
              <Typography variant="body1" component="p" sx={{ color: selectedEntry.notes ? sbg.text.primary : sbg.text.secondary, whiteSpace: 'pre-line' }}>
                {selectedEntry.notes || 'No notes were added to this request.'}
              </Typography>
            </Box>

            {selectedEmployee && (
              <Box sx={{ display: 'grid', gap: 1.5, p: 2, borderRadius: sbg.radius.lg, bgcolor: sbg.bg.surfaceSubtle }}>
                <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>Balances</Typography>
                <BalanceMeters employee={selectedEmployee} />
              </Box>
            )}
          </>
        )}
      </Sheet>

      <ConfirmDialog
        open={Boolean(declineTarget)}
        onClose={() => { if (declineTarget && !inFlight[declineTarget._id]) setDeclineTarget(null); }}
        onConfirm={() => { if (declineTarget) handleDecline(declineTarget); }}
        title={declineTarget ? `Decline ${possessive(declineTarget.employee.name)} ${typeLabel(declineTarget.type)}?` : ''}
        body={
          declineTarget
            ? `${formatDateRange(declineTarget.startDate, declineTarget.endDate)} · ${formatHours(declineTarget.hours)}. The request is marked declined and leaves the approval queue.`
            : undefined
        }
        confirmLabel="Decline request"
        loadingLabel="Declining…"
        destructive
        loading={Boolean(declineTarget && inFlight[declineTarget._id] === 'decline')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? null : 5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeOff;
